import { AirId, stubOfAirId, toRawAirId, AirId as AirIdType, EntityType, RawAirId, Uuid } from '@airreading/common';
import { CopyButton } from './copy-button';
import { ReactNode } from 'react';

// Two usage patterns:
// 1. <AirIdDisplay id={airId} /> where id is AirId<T>
// 2. <AirIdDisplay entityType="cls" rawId={uuidOrRawId} /> where rawId is Uuid | RawAirId
type AirIdDisplayProps =
  | {
      id: AirIdType<EntityType>;
      entityType?: never;
      rawId?: never;
      wrapper?: (displayId: string) => ReactNode;
      showCopyButton?: boolean;
      className?: string;
    }
  | {
      id?: never;
      entityType: EntityType;
      rawId: string | Uuid | RawAirId; // Accepts string during migration period
      wrapper?: (displayId: string) => ReactNode;
      showCopyButton?: boolean;
      className?: string;
    };

/**
 * Displays an Air ID in its display form (stub) with an optional copy button.
 *
 * The component always knows the entity type, ensuring that when users copy an Air ID,
 * they get the full prefixed ID (e.g., 'cls:xxxxx-xxxxx-xxxxxxx-xxxxxxx').
 *
 * @example
 * ```tsx
 * // When you have a full AirId
 * <AirIdDisplay id={studentGroupId} />
 *
 * // When you only have a UUID or RawAirId
 * <AirIdDisplay entityType="cls" rawId={studentGroup.id} />
 * ```
 */
export function AirIdDisplay({ id, entityType, rawId, wrapper, showCopyButton = true, className }: AirIdDisplayProps) {
  // Determine the full AirId and rawId for display
  let fullAirId: AirIdType<EntityType>;
  let displayRawId: RawAirId;

  if (id) {
    // Case 1: We have a full AirId<T>
    fullAirId = id;
    // Extract rawId by parsing
    const parsed = AirId.parse(id);
    displayRawId = parsed.rawId;
  } else if (entityType && rawId) {
    // Case 2: We have entityType and rawId (Uuid | RawAirId)
    // Convert to RawAirId (handles both UUID and RawAirId)
    const rawAirId: RawAirId = toRawAirId(rawId);
    // Construct the full AirId
    fullAirId = AirId(entityType, rawAirId);
    displayRawId = rawAirId;
  } else {
    throw new Error('AirIdDisplay: Must provide either `id` or both `entityType` and `rawId`');
  }

  const displayId = stubOfAirId(displayRawId);
  const idToCopy = fullAirId; // Always copy the full prefixed ID

  const displayElement = wrapper ? wrapper(displayId) : <span>{displayId}</span>;

  return (
    <div className={`flex items-center gap-2 ${className || ''}`}>
      {displayElement}
      {showCopyButton && <CopyButton content={idToCopy} />}
    </div>
  );
}
